'use client'

import React, { useContext } from 'react'
import { Divider, Image, Layout, Typography } from 'antd'
import { ThemeContext } from '@/src/contexts'

import { SocialMediaLinkProps } from '@/src/interfaces'
import { footerMenuItems, socialMediaLinks, legalLinks } from '@/src/data'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '@/src/lib'

const { Link, Text } = Typography
const { Footer: AntDFooter } = Layout

const FooterMenu = () => (
  <div className='flex flex-col mb-8 gap-5 lg:mb-0 print:hidden'>
    {footerMenuItems.map(({ key, href, label, external }) => (
      <div key={key} className='flex group'>
        <Link
          href={href}
          aria-label={label}
          {...(external
            ? { target: '_blank', rel: 'noopener noreferrer' }
            : {})}
          className='flex items-center gap'
        >
          <FontAwesomeIcon
            icon='chevron-right'
            className='mr-2 transition-colors duration-300 group-hover:text-[#274765]'
          />
          <Text className='text-[#274765]'>{label}</Text>
        </Link>
      </div>
    ))}
  </div>
)

const SocialMediaLinks = () => (
  <div className='flex flex-row flex-wrap gap-4 lg:flex-col lg:gap-0 items-start print:hidden'>
    {socialMediaLinks.map(
      ({ key, id, icon, href, label }: SocialMediaLinkProps) => (
        <Link
          key={key}
          href={href}
          aria-label={id}
          data-testid={`${id}`}
          target='_blank'
          rel='noopener noreferrer'
          className='flex items-end mb-2 group'
        >
          <FontAwesomeIcon
            icon={icon}
            className={`text-3xl transition-all duration-300 group-hover:text-[#274765] group-hover:scale-110 ${
              label ? 'mr-3' : ''
            }`}
          />
          {label && <Text type='secondary'>{label}</Text>}
        </Link>
      )
    )}
  </div>
)

const LegalLinks = () => (
  <div className='flex flex-col lg:flex-row justify-center items-center'>
    <div className='mb-2 lg:mb-0 print:mb-0'>
      <Text type='secondary'>
        All Rights Reserved Avalon.AI© {new Date().getFullYear()}
      </Text>
      {/* Print-only footer */}
      <div className='hidden text-sm text-gray-500 print:block'>
        <Text>Date accessed: {new Date().toLocaleDateString()}</Text>
      </div>
      <Divider type='vertical' className='hidden lg:block print:hidden' />
    </div>
    <div className='flex flex-row flex-wrap justify-center items-center gap-2 print:hidden'>
      {legalLinks.map(({ key, href, label }, index) => (
        <React.Fragment key={key}>
          <Link href={href} aria-label={label}>
            {label}
          </Link>
          {index < legalLinks.length - 1 && <Divider type='vertical' />}
        </React.Fragment>
      ))}
    </div>
  </div>
)

export const Footer = () => {
  const { isDarkMode } = useContext(ThemeContext)

  const avalonLogo = isDarkMode
    ? '/assets/logo/AvalonAILogoDark.png'
    : '/assets/logo/AvalonAILogo.png'

  return (
    <AntDFooter data-testid='Footer' style={{ textAlign: 'center' }}>
      <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center py-0 px-4 lg:py-12 lg:px-40 print:p-0'>
        <div className='flex-shrink-0 mb-8 lg:mb-0 print:m-0'>
          <Link
            href='https://avalonai.co/'
            aria-label='Avalon.AI Homepage'
            target='_blank'
            rel='noopener noreferrer'
          >
            <Image
              src={avalonLogo}
              alt='Avalon.AI Logo'
              preview={false}
              height={100}
              width={250}
              style={{ objectFit: 'contain' }}
            />
          </Link>
        </div>
        <FooterMenu />
        <SocialMediaLinks />
      </div>
      <div className='print:hidden'>
        <Divider type='horizontal' />
      </div>
      <LegalLinks />
    </AntDFooter>
  )
}
